import { useCallback, useRef, MutableRefObject } from "react";
import { AuthInterceptor } from "@/auth/AuthInterceptor";
import * as auth from "@/services/auth";
import { NetworkDetector } from "@/services/networkDetector";
import { fetchAccount } from "@/modules/account/service";
import { Account } from "@/types/account";

interface UseSessionManagementParams {
  impersonateCognitoIdRef: MutableRefObject<string | null>;
  setAccount: (account: Account | null) => void;
}

export const useSessionManagement = ({
  impersonateCognitoIdRef,
  setAccount,
}: UseSessionManagementParams) => {
  const refAuthInterceptor = useRef<(() => void) | null>(null);

  const destroyAuthInterceptor = useCallback(() => {
    if (refAuthInterceptor.current) {
      refAuthInterceptor.current();
      refAuthInterceptor.current = null;
    }
  }, []);

  const destroyAccount = useCallback(() => {
    setAccount(null);
  }, [setAccount]);

  const destroySession = useCallback(async () => {
    NetworkDetector.stop();
    destroyAuthInterceptor();
    destroyAccount();
  }, [destroyAuthInterceptor, destroyAccount]);

  const initializeAuthInterceptor = useCallback(async () => {
    if (!auth.isSignedIn()) {
      return;
    }
    const idToken = await auth.getIdToken();
    if (idToken && refAuthInterceptor.current === null) {
      refAuthInterceptor.current = AuthInterceptor(
        idToken,
        () => impersonateCognitoIdRef.current,
      );
    }
  }, [impersonateCognitoIdRef]);

  const initializeAccount = useCallback(async () => {
    try {
      const signedInAccount = await fetchAccount();
      if (signedInAccount !== undefined) {
        setAccount(signedInAccount);
      }
    } catch (err) {
      console.error(err);
      throw err;
    }
  }, [setAccount]);

  const initializeSession = useCallback(async () => {
    await initializeAuthInterceptor();
    await initializeAccount();
    NetworkDetector.start();
  }, [initializeAuthInterceptor, initializeAccount]);

  return {
    destroySession,
    initializeSession,
  };
};
